import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CompanyDto } from '../../types/company';

interface MainPageData {
    selectedCompanyId: number | null;
    selectedBranchId: number | null;
    selectedBranchPersonId: number | null;
    companyPage: number;
    companyPageSize: number;
    companies: CompanyDto[];
    isLoadingMore: boolean;
}

const mainPageDataInitialState: MainPageData = {
    selectedCompanyId: null,
    selectedBranchId: null,
    selectedBranchPersonId: null,
    companyPage: 1,
    companyPageSize: 10,
    companies: [],
    isLoadingMore: false,
};

interface PageState {
    mainPage: MainPageData;
}

const initialState: PageState = {
    mainPage: mainPageDataInitialState,
};

const pageSlice = createSlice({
    name: 'page',
    initialState,
    reducers: {
        setMainPageDataValue: <K extends keyof MainPageData>(
            state: PageState,
            action: PayloadAction<{ key: K; value: MainPageData[K] }>,
        ) => {
            const { key, value } = action.payload;
            state.mainPage[key] = value;
        },
        setMainPageDataPartial: (state: PageState, action: PayloadAction<Partial<MainPageData>>) => {
            Object.entries(action.payload).forEach(([key, value]) => {
                if (key in state.mainPage && value !== undefined) {
                    (state.mainPage as any)[key] = value;
                }
            });
        },
        // create function extendMainPageDataPartial which will keep old values and add new ones
        extendMainPageDataPartial: (state: PageState, action: PayloadAction<Partial<MainPageData>>) => {
            Object.entries(action.payload).forEach(([key, value]) => {
                if (key in state.mainPage && value !== undefined) {
                    if (typeof value === 'number' && typeof (state.mainPage as any)[key] === 'number') {
                        (state.mainPage as any)[key] += value;
                    } else {
                        (state.mainPage as any)[key] = value;
                    }
                }
            });
        },
        setMainPageData: (state, action: PayloadAction<MainPageData>) => {
            state.mainPage = action.payload;
        },
        appendCompanies: (state, action: PayloadAction<CompanyDto[]>) => {
            // Append only new companies (dedupe by id) while preserving order
            const existingIds = new Set(state.mainPage.companies.map((c) => c.id));
            const toAppend = action.payload.filter((c) => !existingIds.has(c.id));
            if (toAppend.length > 0) {
                state.mainPage.companies = [...state.mainPage.companies, ...toAppend];
            }
            state.mainPage.isLoadingMore = false;
        },
        resetCompanies: (state) => {
            state.mainPage.companies = [];
            state.mainPage.companyPage = 1;
            state.mainPage.isLoadingMore = false;
        },
        setLoadingMore: (state, action: PayloadAction<boolean>) => {
            state.mainPage.isLoadingMore = action.payload;
        },
    },
});

export const {
    setMainPageData,
    setMainPageDataPartial,
    extendMainPageDataPartial,
    setMainPageDataValue,
    appendCompanies,
    resetCompanies,
    setLoadingMore,
} = pageSlice.actions;
export default pageSlice.reducer;
